/**
 * EL CUADRO EN GRANDE, a pantalla completa.
 *
 * QUÉ PROBLEMA RESUELVE. Un cuadro de dieciséis no entra en el ancho de un
 * teléfono, y adentro de la ficha del torneo entra todavía menos: la tarjeta
 * tiene bordes, relleno y un ancho máximo, así que de los 390 px de la
 * pantalla al cuadro le llegaban unos 300. Se veían dos rondas y media y
 * había que arrastrar mucho. Acá el cuadro usa la pantalla entera.
 *
 * ES UNA DIRECCIÓN Y NO UNA VENTANA. `/torneos/:id/cuadro` se puede mandar
 * por WhatsApp —«mirá el cuadro»— y el botón de atrás del navegador funciona
 * como todo el mundo espera. Una ventana encima de la ficha no se puede
 * compartir y el botón de atrás la cierra sin volver a ningún lado.
 *
 * NO LLEVA NI BARRA DE ARRIBA NI PESTAÑAS, que es de dónde sale el espacio:
 * juntas se comen unos 100 px de alto. Por eso el botón de VOLVER es
 * obligatorio y no decorativo — sin él, esta pantalla no tendría salida.
 *
 * EL CUADRO SE MIRA; LA AGENDA SE ANOTA.
 *
 * El dibujo del cuadro no se toca: se abre para verlo entero de un vistazo, y
 * un partido que además es un botón en un cuadro de 128 es un botón que se
 * aprieta sin querer al arrastrar. Pero la agenda de abajo sí — es el mismo
 * renglón que dice «Beto vs Ana, el sábado a las 9 en la cancha 2», y cuando
 * terminan de jugar lo que hay que hacer con ese renglón es escribir el
 * marcador. Mandar a alguien a buscar esa misma llave adentro del cuadro para
 * anotar lo que ya tiene delante es el paso que hace que nadie anote.
 *
 * NO SON DOS LUGARES DONDE ARREGLAR EL MISMO ERROR: el editor es el mismo
 * `ScoreEditor` de la ficha, el permiso es el mismo `match.canScore` que
 * llena el servidor, y el guardado es la misma llamada. Lo único propio de
 * acá es dónde aparece.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import type { CategoryBracket, Match, MatchScore, Tournament } from '@tennisup/contracts';
import { categoryKey } from '@tennisup/contracts';

import { Alert } from '../components/Alert';
import { BackButton } from '../components/BackButton';
import { Bracket, byRound } from '../components/Bracket';
import {
  BracketSearch,
  llevarAlPartido,
  partidoMásAvanzado,
} from '../components/BracketSearch';
import { Groups } from '../components/Groups';
import { NextMatches, nombreDeRonda, rondaEnJuego } from '../components/NextMatches';
import { ScoreEditor, sidesOfMatch } from '../components/ScoreEditor';
import { SponsorCarousel } from '../components/SponsorCarousel';
import { LinkIcon } from '../components/icons';
import { api } from '../lib/api';
import { errorMessage } from '../lib/errorMessage';
import { categoryName } from '../lib/categoryName';

export function BracketFullPage(): JSX.Element {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [brackets, setBrackets] = useState<CategoryBracket[]>([]);
  const [matches, setMatches] = useState<Match[]>([]);
  const [error, setError] = useState<string | null>(null);
  /** A quién se buscó, para señalar sus llaves. Ver `BracketSearch`. */
  const [destacado, setDestacado] = useState<string | null>(null);
  /** El partido cuyo marcador se está escribiendo, si hay alguno. */
  const [anotando, setAnotando] = useState<Match | null>(null);

  const load = useCallback(async () => {
    try {
      const [ficha, cuadros, partidos] = await Promise.all([
        api.findTournament(id),
        api.tournamentBrackets(id),
        api.tournamentMatches(id),
      ]);
      setTournament(ficha);
      setBrackets(cuadros);
      setMatches(partidos);
    } catch (caught) {
      setError(errorMessage(t, caught));
    }
  }, [id, t]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * GUARDAR Y VOLVER A PEDIR TODO.
   *
   * Anotar un resultado mueve al ganador a la ronda siguiente, puede terminar
   * la categoría y cambia qué partidos quedan por jugar. Reconstruir eso a
   * mano en la pantalla sería tener dos ideas distintas de cómo va el torneo,
   * y una de las dos estaría mal.
   */
  async function guardar(
    match: Match,
    score: MatchScore,
    noShowEntryId: string | null,
  ): Promise<void> {
    await api.saveMatchScore(id, match.id, score, noShowEntryId);
    setAnotando(null);
    await load();
  }

  /** La foto del partido. Aparte del marcador: son dos cosas distintas. */
  async function guardarFoto(match: Match, file: File | null): Promise<void> {
    if (file === null) await api.removeMatchPhoto(id, match.id);
    else await api.setMatchPhoto(id, match.id, file);
    await load();
  }

  /**
   * LLEVAR LA PANTALLA HASTA LA LLAVE, después de dibujarla.
   *
   * En un efecto y no en el `onClick` del nombre: al apretar, las clases
   * todavía no están puestas y la llave puede estar en una columna que
   * cambió de alto. El efecto corre con el dibujo ya hecho.
   */
  useEffect(() => {
    llevarAlPartido(partidoMásAvanzado(matches, destacado));
  }, [matches, destacado]);

  return (
    <div className="full-bracket">
      {/* LA BARRA DE VOLVER ES LO ÚNICO FIJO. Se queda arriba mientras el
          cuadro se arrastra de lado: en una pantalla sin pestañas, perder de
          vista la salida mientras uno se mueve por un cuadro de 32 es quedar
          encerrado. */}
      <header className="full-bracket-bar">
        <BackButton to={`/torneos/${id}`} />
        {tournament !== null && <h1>{tournament.name}</h1>}
        {/* EN QUÉ ANDA EL TORNEO, EN LA BARRA QUE NO SE VA.
            Es la primera pregunta de quien abre un cuadro por un enlace que
            le pasaron: si esto ya pasó o todavía se está jugando. Un cuadro
            a medio llenar y uno terminado en el que ganó el favorito se ven
            casi igual, y no son lo mismo. */}
        {/* EL ENLACE PARA MANDAR POR WHATSAPP.
            Lleva a la vista pública, que es la que puede abrir alguien sin
            cuenta. Es un enlace y no un botón de «copiar»: apretándolo se
            llega a la página, y de ahí la dirección está en la barra del
            navegador y el botón de compartir del teléfono ya funciona. Un
            «copiado ✓» que no se ve en ningún lado es peor que eso. */}
        {tournament !== null && (
          <Link className="full-bracket-share" to={`/cuadro/${id}`} aria-label={t('bracket.shareLink')}>
            {/* EL ÍCONO SIEMPRE, LA PALABRA CUANDO HAY LUGAR. A 390 px la
                barra reparte el ancho entre «Volver», el nombre del torneo,
                esto y la etiqueta de estado, y «Primavero Mujeres» quedaba en
                «Pri…». El nombre del torneo es el dato; esto es un botón, y
                un botón se reconoce por su forma. El `aria-label` dice lo
                mismo siempre, se vea la palabra o no. */}
            <LinkIcon title="" size={16} />
            <span className="full-bracket-share-text">{t('bracket.shareLink')}</span>
          </Link>
        )}
        {tournament !== null && (
          <span className={`tag tag-${tournament.status.toLowerCase()}`}>
            {t(`tournamentStatus.${tournament.status}`)}
          </span>
        )}
      </header>

      {error !== null && (
        <div className="full-bracket-body">
          <Alert kind="error">{error}</Alert>
        </div>
      )}

      {tournament === null ? (
        error === null ? (
          <p className="help full-bracket-body">{t('common.loading')}</p>
        ) : null
      ) : (
        <div className="full-bracket-body">
          {/*
            LOS PATROCINADORES, ANTES QUE NADA.

            Son quienes pagan el torneo, y esta es la pantalla que más se
            mira: el cuadro se abre veinte veces por partido. Arriba del
            primer cuadro y no al pie —al pie no llega nadie en un cuadro de
            128— y arriba también del buscador, que es una herramienta que uno
            va a buscar cuando la necesita.

            El mismo carrusel de la ficha del torneo y no uno propio: una
            segunda forma de dibujar un logo es una que tarde o temprano lo
            recorta distinto.
          */}
          <SponsorCarousel sponsors={tournament.sponsors} />

          {/* EL BUSCADOR, ARRIBA DE TODOS LOS CUADROS Y UNO SOLO. Junta las
              categorías a propósito: quien se busca a sí mismo no sabe —ni
              tiene por qué— en cuál de los cuadros cae. */}
          {visibles(tournament).length > 0 && (
            <BracketSearch matches={matches} destacado={destacado} onPick={setDestacado} />
          )}

          {visibles(tournament).length === 0 ? (
            // Sin ningún cuadro tirado no hay nada que agrandar, y decirlo es
            // mejor que una pantalla en blanco con un botón de volver.
            <p className="help">{t('bracket.noneStarted')}</p>
          ) : (
            visibles(tournament).map((category) => {
              const clave = categoryKey(category);
              const delCuadro = matches.filter(
                (m) => categoryKey(m) === categoryKey(category),
              );
              const bracket = brackets.find(
                (b) => categoryKey(b) === categoryKey(category),
              );
              const inscritos = tournament.entries
                .filter((e) => categoryKey(e) === categoryKey(category))
                .sort((a, b) => a.seed - b.seed);

              return (
                <section key={clave}>
                  <h2>
                    {categoryName(t, category)}
                  </h2>
                  {/* Y en qué ronda va ESTA categoría. El estado del torneo es
                      uno solo, pero un torneo con cuatro categorías tiene la A
                      en la final y la C en cuartos. */}
                  <EnQueVa matches={delCuadro} />

                  {category.format !== 'KNOCKOUT' ? (
                    <Groups
                      format={category.format}
                      tournamentName={tournament.name}
                      matches={delCuadro}
                      entries={inscritos}
                      deadlines={tournament.roundDeadlines.filter(
                        (d) => categoryKey(d) === categoryKey(category),
                      )}
                    />
                  ) : bracket === undefined || bracket.problem !== null ? (
                    <p className="help">{t('bracket.notPossible')}</p>
                  ) : (
                    <Bracket
                      matches={delCuadro}
                      tournamentName={tournament.name}
                      destacado={destacado}
                      deadlines={tournament.roundDeadlines.filter(
                        (d) => categoryKey(d) === categoryKey(category),
                      )}
                    />
                  )}
                </section>
              );
            })
          )}

          {/* LA AGENDA, DEBAJO DE TODOS LOS CUADROS Y UNA SOLA VEZ.
              Junta las categorías a propósito: quien viene el sábado quiere
              saber qué se juega el sábado, no en qué categoría cae. */}
          {visibles(tournament).length > 0 && (
            <NextMatches
              matches={matches}
              deadlines={tournament.roundDeadlines}
              onScore={setAnotando}
              debajo={(match) =>
                anotando?.id === match.id ? (
                  <ScoreEditor
                    {...sidesOfMatch(anotando)}
                    onSave={(score, noShow) => guardar(anotando, score, noShow)}
                    onPhoto={(file) => guardarFoto(anotando, file)}
                    onCancel={() => setAnotando(null)}
                  />
                ) : null
              }
            />
          )}
        </div>
      )}
    </div>
  );
}

/**
 * EN QUÉ RONDA VA ESTA CATEGORÍA.
 *
 * No sale del estado del torneo —ese es uno solo para todas— sino de los
 * partidos: la ronda más baja que todavía tiene alguno sin resultado. Cuando
 * no queda ninguno, la categoría terminó y eso también se dice: un cuadro
 * lleno sin una palabra encima obliga a buscar la final con el ojo para saber
 * si ya está.
 */
function EnQueVa({ matches }: { matches: readonly Match[] }): JSX.Element | null {
  const { t } = useTranslation();
  const ronda = rondaEnJuego(matches);
  const columnas = byRound(matches);
  if (columnas.length === 0) return null;

  if (ronda === null) {
    return <p className="help full-bracket-round">{t('tournamentStatus.FINISHED')}</p>;
  }

  return (
    <p className="help full-bracket-round">
      {t('bracket.playing', { round: nombreDeRonda(t, 2 ** columnas.length, ronda) })}
    </p>
  );
}

/**
 * QUÉ CUADROS SE MUESTRAN EN GRANDE: los que ya se tiraron.
 *
 * Antes de tirarlo el cuadro es una PREVISIÓN que cambia con cada
 * inscripción y con cada sorteo. En la ficha se la ve quien organiza, porque
 * es lo que está armando; acá no, porque esta pantalla existe para mirar el
 * cuadro de un vistazo y una previsión mirada así se lee como definitiva.
 */
function visibles(tournament: Tournament): Tournament['categories'] {
  return tournament.categories.filter((category) => category.startedAt !== null);
}
